import { useTranslation } from 'react-i18next'
import type { ContentLocale } from '@/data/public-catalog'
import { pointOfCareProducts, type PointOfCareProductKey } from '@/data/product-pages/point-of-care'
import { ProductMedia } from './ProductMedia'

function Accre8Body({ language }: { language: ContentLocale }) {
  const { t } = useTranslation('common')
  const text = (key: string, options?: Record<string, unknown>) => t(`productPage.accre8.${key}`, { lng: 'it', ...options })
  const paragraphs = text('paragraphs', { returnObjects: true }) as unknown as string[]
  const assays = text('assays', { returnObjects: true }) as unknown as Array<{ title: string; values: string }>
  return <section className="shell-container product-section poct-body accre8-body" aria-labelledby="accre8-body-title" lang={language === 'en' ? 'it' : undefined}>
    <div className="product-copy accre8-copy">
      <h2 id="accre8-body-title">{text('bodyTitle')}</h2>
      {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
    </div>
    <section className="accre8-menu" aria-labelledby="accre8-menu-title">
      <h2 id="accre8-menu-title">{text('assaysTitle')}</h2>
      <dl>{assays.map(assay => <div key={assay.title}><dt>{assay.title}</dt><dd>{assay.values}</dd></div>)}</dl>
    </section>
  </section>
}

function EasyNatBody({ language }: { language: ContentLocale }) {
  const { t } = useTranslation('common')
  const paragraphs = t('productPage.easyNat.paragraphs', { lng: language, returnObjects: true }) as string[]
  const method = t('productPage.easyNat.method', { lng: language, returnObjects: true }) as string[]
  const media = pointOfCareProducts['prd-cpa-easynat-real-time-molecular-diagnostic-system-vet'].supportingMedia
  const respiratory = language === 'en'
    ? t('productPage.easyNat.respiratoryTests', { lng: 'en', returnObjects: true }) as string[]
    : []
  const sexualHealth = language === 'en'
    ? t('productPage.easyNat.sexualHealthTests', { lng: 'en', returnObjects: true }) as string[]
    : []
  return <section className="product-section poct-body easynat-body" aria-labelledby="easynat-body-title" lang={language}>
    <div className="shell-container easynat-intro">
      <div className="product-copy">
        <h2 id="easynat-body-title">{t('productPage.easyNat.bodyTitle')}</h2>
        {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
      </div>
      <ProductMedia path={media[0]} altKey="productPage.easyNat.kitAlt" className="easynat-kit" />
    </div>
    <section className="easynat-workflow" aria-labelledby="easynat-workflow-title">
      <div className="shell-container">
        <h2 id="easynat-workflow-title">{t('productPage.easyNat.methodTitle')}</h2>
        <ol>{method.map(step => <li key={step}>{step}</li>)}</ol>
        <ProductMedia path={media[1]} altKey="productPage.easyNat.workflowAlt" className="easynat-workflow-media" />
      </div>
    </section>
    <div className="shell-container easynat-panel">
      <div>
        <h2>{t('productPage.easyNat.panelTitle')}</h2>
        {language === 'en'
          ? <div className="easynat-test-groups">
              <section><h3>{t('productPage.easyNat.respiratoryTitle')}</h3><ul>{respiratory.map(test => <li key={test}>{test}</li>)}</ul></section>
              <section><h3>{t('productPage.easyNat.sexualHealthTitle')}</h3><ul>{sexualHealth.map(test => <li key={test}>{test}</li>)}</ul></section>
            </div>
          : <p>{t('productPage.easyNat.panelIntro')}</p>}
      </div>
      <ProductMedia path={media[2]} altKey="productPage.easyNat.panelAlt" className="easynat-panel-media" />
    </div>
  </section>
}

function Poch100iBody({ language }: { language: ContentLocale }) {
  const { t } = useTranslation('common')
  const text = (key: string, options?: Record<string, unknown>) => t(`productPage.poch100i.${key}`, { lng: 'it', ...options })
  const paragraphs = text('paragraphs', { returnObjects: true }) as unknown as string[]
  const metrics = text('metrics', { returnObjects: true }) as unknown as Array<{ value: string; label: string }>
  const parameterGroups = text('parameterGroups', { returnObjects: true }) as unknown as Array<{ title: string; values: string }>
  return <section className="shell-container product-section poct-body poch-body poch100i-body" aria-labelledby="poch-body-title" lang={language === 'en' ? 'it' : undefined}>
    <div className="product-copy poch-copy">
      <h2 id="poch-body-title">{text('bodyTitle')}</h2>
      {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
    </div>
    <dl className="poch-metrics">{metrics.map(metric => <div key={metric.value}><dt>{metric.value}</dt><dd>{metric.label}</dd></div>)}</dl>
    <section className="poch-parameters" aria-labelledby="poch-parameters-title">
      <h2 id="poch-parameters-title">{text('parametersTitle')}</h2>
      <dl>{parameterGroups.map(group => <div key={group.title}><dt>{group.title}</dt><dd>{group.values}</dd></div>)}</dl>
    </section>
  </section>
}

function Sd1Body({ language }: { language: ContentLocale }) {
  const { t } = useTranslation('common')
  const text = (key: string, options?: Record<string, unknown>) => t(`productPage.sd1.${key}`, { lng: 'it', ...options })
  const paragraphs = text('paragraphs', { returnObjects: true }) as unknown as string[]
  const media = pointOfCareProducts['prd-analizzatore-biochimico-a-secco-automatico-sd1'].supportingMedia
  return <section className="shell-container product-section poct-body sd1-body" aria-labelledby="sd1-body-title" lang={language === 'en' ? 'it' : undefined}>
    <div className="product-copy sd1-copy">
      <h2 id="sd1-body-title">{text('bodyTitle')}</h2>
      {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
    </div>
    <section className="sd1-table" aria-labelledby="sd1-table-title">
      <h2 id="sd1-table-title">{text('tableTitle')}</h2>
      <p>{text('tableIntro')}</p>
      <ProductMedia path={media[0]} altKey="productPage.sd1.tableAlt" />
    </section>
  </section>
}

function Sg1Body({ language }: { language: ContentLocale }) {
  const { t } = useTranslation('common')
  const text = (key: string, options?: Record<string, unknown>) => t(`productPage.sg1.${key}`, { lng: 'it', ...options })
  const paragraphs = text('paragraphs', { returnObjects: true }) as unknown as string[]
  const panels = text('panels', { returnObjects: true }) as unknown as Array<{ title: string; values: string }>
  const media = pointOfCareProducts['prd-sg1'].supportingMedia
  return <section className="shell-container product-section poct-body sg1-body" aria-labelledby="sg1-body-title" lang={language === 'en' ? 'it' : undefined}>
    <div className="product-copy sg1-copy">
      <h2 id="sg1-body-title">{text('bodyTitle')}</h2>
      {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
    </div>
    <section className="sg1-panels" aria-labelledby="sg1-panels-title">
      <div><h2 id="sg1-panels-title">{text('panelsTitle')}</h2><dl>{panels.map(panel => <div key={panel.title}><dt>{panel.title}</dt><dd>{panel.values}</dd></div>)}</dl></div>
      <ProductMedia path={media[0]} altKey="productPage.sg1.cartridgeAlt" className="sg1-cartridge" />
    </section>
  </section>
}

export function PointOfCareBody({ productKey, language }: { productKey: PointOfCareProductKey; language: ContentLocale }) {
  if (productKey === 'accre8') return <Accre8Body language={language} />
  if (productKey === 'easyNat') return <EasyNatBody language={language} />
  if (productKey === 'poch100i') return <Poch100iBody language={language} />
  if (productKey === 'sd1') return <Sd1Body language={language} />
  return <Sg1Body language={language} />
}
